// components
import { AddWidgetTile } from './AddWidgetTile';
import { SortableTask } from './SortableTask';
import { DashboardMainTile } from './DashboardMainTile';

// hooks
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'hooks';
import { useTasksData } from './hooks/useTasksData';
import { useTilesLocalStorage } from './hooks/useTilesLocalStorage';

// types
import { TasksProps } from './TasksList.types';
import { TabType } from '../Home.enums';

// utils
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import dayjs from 'dayjs';
import { getAllMaxItems, restrictToElement } from './TasksList.utils';
import { CircularProgress } from '@mui/material';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useShallow } from 'zustand/shallow';

import { usePlannerMessagesStore } from '../messages/store';
import { PlannerTaskTab } from '../messages/types';
import {
  LEAVE_REQUEST_TASK_KEYS,
  DUTY_CHANGE_TASK_KEYS,
  FLEX_NOTIFICATION_TASK_KEYS
} from '../messages/constants';

export default function Tasks({
  selectedPlanUnits = [],
  openTab,
  containerRef,
  amountOfOpenDuties
}: TasksProps) {
  const {
    hasFeatureToggleExpenseClaims,
    hasTravelMovementsFeature,
    hasProcessEndOfEmploymentSettlementAccess,
    hasDayLimitViolationsViewAccess,
    hasAdditionalCompensationsViewAccess
  } = useSelector(({ grants }) => grants);
  const setMessagesTab = usePlannerMessagesStore((s) => s.setTab);

  const { hiddenTiles, setHiddenTiles, tilesOrder, setTilesOrder } =
    useTilesLocalStorage();

  const {
    data,
    setData,
    isLoading,
    startDateDayLimitViolations,
    endDateDayLimitViolations,
    startDateAdditionalCompensations,
    endDateAdditionalCompensations
  } = useTasksData({
    selectedPlanUnits,
    tilesOrder,
    hasProcessEndOfEmploymentSettlementAccess,
    hasFeatureToggleExpenseClaims,
    hasDayLimitViolationsViewAccess,
    hasAdditionalCompensationsViewAccess
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const hasTravelExpenseClaimsAndTravelMovements =
    hasFeatureToggleExpenseClaims && hasTravelMovementsFeature;

  const { setSelectedDateRange } = useDateNavigationHoursStore(
    (state) => state
  );
  const setPeriod = useDateNavigationStore(
    useShallow((state) => state.setPeriod)
  );

  const modifiers = [restrictToElement(containerRef.current)];

  const { visibleData, visibleIds } = useMemo(() => {
    const visibleData = data.filter((item) => !hiddenTiles.includes(item.key));
    return { visibleData, visibleIds: visibleData.map((item) => item.key) };
  }, [data, hiddenTiles]);

  const importantTasks = useMemo(() => getAllMaxItems(data), [data]);

  const hasHiddenTiles = useMemo(
    () => data.some((item) => hiddenTiles.includes(item.key)),
    [data, hiddenTiles]
  );

  useEffect(() => {
    if (!data.length) return;
    const currentKeys = data.map((item) => item.key);

    setTilesOrder((prevOrder) => {
      if (!prevOrder.length) return currentKeys;
      const newKeys = currentKeys.filter((key) => !prevOrder.includes(key));
      return newKeys.length ? [...prevOrder, ...newKeys] : prevOrder;
    });

    setHiddenTiles((prev) => {
      const filtered = prev.filter((key) => currentKeys.includes(key));
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [data, setTilesOrder, setHiddenTiles]);

  const onTaskClicked = useCallback(
    (definitionKey: string) => {
      const simpleTabMap: Partial<Record<string, TabType>> = {
        endOfEmploymentsSettlements: TabType.END_OF_EMPLOYMENT_SETTLEMENTS
      };

      if (simpleTabMap[definitionKey]) {
        openTab(simpleTabMap[definitionKey]!);
        return;
      }

      if (
        definitionKey === 'expenseClaimsUnapprovedLastMonth' ||
        definitionKey === 'expenseClaimsUnapprovedCurrentMonth'
      ) {
        const isLastMonth =
          definitionKey === 'expenseClaimsUnapprovedLastMonth';
        const dateOfMonth = isLastMonth
          ? dayjs().subtract(1, 'month').startOf('month').toDate()
          : dayjs().startOf('month').toDate();

        openTab(
          hasTravelExpenseClaimsAndTravelMovements || hasTravelMovementsFeature
            ? TabType.TRAVEL_MOVEMENTS
            : TabType.TRAVEL_EXPENSE_CLAIMS,
          { dateOfMonth }
        );
        return;
      }

      if (definitionKey === 'dayLimitViolations') {
        setSelectedDateRange([
          startDateDayLimitViolations,
          endDateDayLimitViolations
        ]);
        setPeriod('custom');
        openTab(TabType.DAY_LIMIT_VIOLATIONS);
        return;
      }

      if (definitionKey === 'additionalCompensations') {
        setSelectedDateRange([
          startDateAdditionalCompensations,
          endDateAdditionalCompensations
        ]);
        setPeriod('custom');
        openTab(TabType.ADDITIONAL_COMPENSATIONS);
        return;
      }

      const leaveKeys = new Set<string>(LEAVE_REQUEST_TASK_KEYS);
      const dutyKeys = new Set<string>(DUTY_CHANGE_TASK_KEYS);
      const flexKeys = new Set<string>(FLEX_NOTIFICATION_TASK_KEYS);

      let tab = PlannerTaskTab.ALL;
      if (leaveKeys.has(definitionKey)) tab = PlannerTaskTab.LEAVE_REQUESTS;
      else if (dutyKeys.has(definitionKey)) tab = PlannerTaskTab.DUTY_CHANGES;
      else if (flexKeys.has(definitionKey))
        tab = PlannerTaskTab.FLEX_NOTIFICATIONS;

      setMessagesTab(tab);
      openTab(TabType.MESSAGES);
    },
    [
      openTab,
      hasTravelExpenseClaimsAndTravelMovements,
      hasTravelMovementsFeature,
      startDateDayLimitViolations,
      endDateDayLimitViolations,
      startDateAdditionalCompensations,
      endDateAdditionalCompensations,
      setSelectedDateRange,
      setPeriod,
      setMessagesTab
    ]
  );

  const onRemoveTile = useCallback(
    (key: string) =>
      setHiddenTiles((prev) => (prev?.includes(key) ? prev : [...prev, key])),
    [setHiddenTiles]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // if there's no valid drop target (over is null) OR
      // the dragged item was dropped back in the same spot, exit early, no changes needed
      if (!over || active.id === over.id) return;

      // update the visual data order after drag-and-drop
      setData((prev) => {
        // find the index of the dragged item
        const oldIndex = prev.findIndex((d) => d.key === active.id);

        // find the index of the drop target item
        const newIndex = prev.findIndex((d) => d.key === over.id);

        // if either index wasn't found, do nothing
        if (oldIndex === -1 || newIndex === -1) return prev;

        // return a new array with the dragged item moved to its new position
        return arrayMove(prev, oldIndex, newIndex);
      });

      // update the order state that’s stored in localStorage (used for persistence)
      setTilesOrder((prev) => {
        // find the index of the dragged item
        const oldIndex = prev.indexOf(String(active.id));

        // find the index of the drop target item
        const newIndex = prev.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return prev;

        // reorder the keys array in localStorage for persistent sorting
        return arrayMove(prev, oldIndex, newIndex);
      });
    },
    [setData, setTilesOrder]
  );

  return (
    <>
      <DashboardMainTile
        count={amountOfOpenDuties}
        importantTasks={importantTasks}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={modifiers}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleIds} strategy={rectSortingStrategy}>
          {isLoading ? (
            <CircularProgress sx={{ gridColumn: '1 / -1', margin: 'auto' }} />
          ) : (
            visibleData.map(({ key, items }) => (
              <SortableTask
                key={key}
                dataKey={key}
                items={items}
                onRemoveTile={onRemoveTile}
                onTaskClicked={onTaskClicked}
              />
            ))
          )}
        </SortableContext>
      </DndContext>
      {hasHiddenTiles && (
        <AddWidgetTile
          data={data}
          hiddenData={hiddenTiles}
          setHiddenData={setHiddenTiles}
        />
      )}
    </>
  );
}
